import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { mergeMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';
import * as NoteActions from '../actions/note.actions';
import * as UiActions from '../../ui/actions/ui.actions';
import { AppState } from '../../app.state';
import { NoteService } from '../services/note.service';
import { Note, NoteFilters } from '../models/note.model';
import { selectActiveNoteFilters } from '../selectors/note.selectors';
import { MessageType } from '../../payment/models/payroll.model';

@Injectable()
export class NoteEffects {
  constructor(
    private actions$: Actions,
    private noteService: NoteService,
    private store: Store<AppState>
  ) { }

  // Get All Notes - only reload if filters are different from active filters
  getAllNotes$ = createEffect(() => this.actions$.pipe(
    ofType(NoteActions.getAllNotes),
    withLatestFrom(this.store.select(selectActiveNoteFilters)),
    filter(([{ filters }, activeFilters]) => {
      // Compare filters to determine if we need to reload
      const newFilters = filters || {};
      return !this.areFiltersEqual(newFilters, activeFilters);
    }),
    mergeMap(([{ organizationId, filters }]) => {
      const appliedFilters = filters || {};
      return this.noteService.getAllNotes(organizationId, appliedFilters).pipe(
        map((notes: Note[]) => NoteActions.getAllNotesSuccess({ notes, filters: appliedFilters })),
        catchError(error => 
          of(UiActions.serviceCallFailure({ action: NoteActions.getAllNotes, error }))
        )
      );
    })
  ));

  // Create Note
  createNote$ = createEffect(() => this.actions$.pipe(
    ofType(NoteActions.createNote),
    mergeMap(({ organizationId, note }) => 
      this.noteService.createNote(organizationId, note).pipe(
        map((note: Note) => NoteActions.createNoteSuccess({ note })),
        catchError(error => 
          of(UiActions.serviceCallFailure({ action: NoteActions.createNote, error }))
        )
      )
    )
  ));

  // Update Note
  updateNote$ = createEffect(() => this.actions$.pipe(
    ofType(NoteActions.updateNote),
    mergeMap(({ organizationId, noteId, note }) => 
      this.noteService.updateNote(organizationId, noteId, note).pipe(
        map((note: Note) => NoteActions.updateNoteSuccess({ note })),
        catchError(error => 
          of(UiActions.serviceCallFailure({ action: NoteActions.updateNote, error }))
        )
      )
    )
  ));

  // Close Note
  closeNote$ = createEffect(() => this.actions$.pipe(
    ofType(NoteActions.closeNote),
    mergeMap(({ noteId, organizationId }) => {
      console.log('Effect caught closeNote action for:', noteId, 'org:', organizationId);
      return this.noteService.closeNote(noteId, organizationId).pipe(
        map((note: Note) => {
          console.log('Successfully closed note:', noteId);
          return NoteActions.closeNoteSuccess({ note });
        }),
        catchError(error => {
          console.error('Failed to close note:', error);
          return of(NoteActions.closeNoteFailure({ error }));
        })
      );
    })
  ));

  /**
   * Compare two filter objects to determine if they are equal
   */
  private areFiltersEqual(filters1: NoteFilters, filters2: NoteFilters): boolean {
    return (
      filters1.group === filters2.group &&
      filters1.startDate === filters2.startDate &&
      filters1.endDate === filters2.endDate
    );
  }
}
